import { UserDTO } from '@/data/dto/user.dto';
import { Route, RouteConfig } from 'vue-router';

export default class RouterUtil {
  static getNavigationRoutes(routes: RouteConfig[]): RouteConfig[] {
    if (!routes) return [];
    return routes.filter((route: RouteConfig) => {
      if (route.children) {
        route.children = RouterUtil.getNavigationRoutes(route.children);
      }
      return RouterUtil.isVisibleOnNavigation(route);
    });
  }

  private static normalizePath(path: string): string {
    return path.replace(/\/+$/, '');
  }

  // Compara o path absoluto, resolvendo paths relativos de rotas filhas a partir do pai
  static getRouteByPath(
    path: string,
    routes: RouteConfig[] = [],
    basePath = ''
  ): RouteConfig | undefined {
    if (!path) return undefined;
    const target = RouterUtil.normalizePath(path);
    for (const route of routes) {
      const fullPath = RouterUtil.normalizePath(
        route.path.startsWith('/') ? route.path : `${basePath}/${route.path}`
      );
      if (fullPath === target) return { ...route };
      if (route.children) {
        const child = RouterUtil.getRouteByPath(
          path,
          route.children,
          fullPath
        );
        if (child) return { ...child };
      }
    }
  }

  static getChildrenByParentPath(
    path: string,
    routes: RouteConfig[]
  ): RouteConfig[] {
    const currentRoute = RouterUtil.getRouteByPath(path, routes);
    return currentRoute && currentRoute.children
      ? [...currentRoute.children]
      : [];
  }

  static getChildrenByParentName(name: string, routes: any[]): any | null {
    for (const route of routes) {
      if (route.name === name) return route;
      if (route.children) {
        const found = RouterUtil.getChildrenByParentName(name, route.children);
        if (found) return found;
      }
    }
    return null;
  }

  static getRouteById(
    id: number,
    routes: RouteConfig[]
  ): RouteConfig | undefined {
    for (const route of routes) {
      if (route.meta && route.meta.id === id) {
        return route;
      }
      // Recursively search in children
      if (route.children) {
        const found = RouterUtil.getRouteById(id, route.children);
        if (found) return found;
      }
    }

    return undefined;
  }

  static isVisibleOnNavigation = (route: RouteConfig) =>
    !route.meta ||
    (!route.meta.hiddenOnNavigation &&
      !RouterUtil.isVisibleToNotAllowedUsers(route));

  static isVisibleToNotAllowedUsers = (route: RouteConfig) => {
    return (
      route.meta &&
      route.meta.hiddenToNotAllowedUsers &&
      !route.meta.userAllowed
    );
  };

  // Usa os records já resolvidos pelo vue-router; pai inativo ou restrito protege seus filhos
  static isRouteAllowed = (to: Route, user?: UserDTO): boolean =>
    !!user &&
    to.matched.length > 0 &&
    to.matched.every(
      (record) =>
        record.meta?.active !== false &&
        RouterUtil.userHasPermission(record as unknown as RouteConfig, user)
    );

  static routeRequiresPermission = (route: RouteConfig) =>
    route.meta && route.meta.requiresPermission;

  static identifyUserAllowedRoutes(
    user: UserDTO,
    routes: RouteConfig[]
  ): RouteConfig[] {
    if (!routes) return [];
    return routes.map((route) => {
      if (route.children) {
        route.children = RouterUtil.identifyUserAllowedRoutes(
          user,
          route.children
        );
      }
      if (!route.meta) route.meta = {};
      route.meta.userAllowed =
        !RouterUtil.routeRequiresPermission(route) ||
        RouterUtil.userHasPermission(route, user);
      return route;
    });
  }

  static userHasPermission = (route: RouteConfig, user: UserDTO) => {
    if (!route.meta || !route.meta.requiresPermission) return true;
    return (
      user.policies &&
      Object.keys(user.policies).includes(route.meta.requiresPermission)
    );
  };

  static getRouteLabelAsString(route: RouteConfig): string {
    if (!route.meta || !route.meta.labels) return '';

    if (Array.isArray(route.meta.labels)) {
      return route.meta.labels.join(' ');
    }

    return route.meta.labels;
  }
}
